/**
 * Resolves the connection-side facts needed for managed-API `dynamicInvoke`
 * routing by fetching the connection ARM resource + project-scoped connector
 * once. Mirrors LogicAppsUX's `await getConnection(...)` pattern via the
 * `isReady` flag (Otto's request-build is sync, can't await).
 */

import { useMemo } from 'react';
import { useConnection } from '@/hooks/useConnectionMutations';
import { useConnector } from '@/hooks/useConnector';
import { extractConnectionName } from '@/services/connectionApi';
import { isManagedApiConnector } from '@/utils/connections';
import { isManagedIdentityConnection } from '@/services/dynamicConnectionService';

export interface ManagedConnectionInfo {
  isManagedIdentity: boolean;
  /** Live runtime URL from the connection ARM resource. */
  connectionRuntimeUrl: string | undefined;
  /** True once both queries have settled (or aren't needed). Gate dynamicInvoke on this. */
  isReady: boolean;
}

export function useManagedConnectionInfo(
  connectorId: string | undefined | null,
  connectionId: string | undefined | null
): ManagedConnectionInfo {
  const enabled = !!connectorId && !!connectionId && isManagedApiConnector(connectorId);

  const connectionName = useMemo(
    () => (enabled && connectionId ? extractConnectionName(connectionId) : ''),
    [enabled, connectionId]
  );

  const { data: connection, isPending: connectionPending } = useConnection(
    connectionName,
    enabled && !!connectionName
  );
  const { data: connector, isPending: connectorPending } = useConnector(enabled ? connectorId : null);

  return useMemo(() => {
    if (!enabled) return { isManagedIdentity: false, connectionRuntimeUrl: undefined, isReady: true };
    return {
      isManagedIdentity: isManagedIdentityConnection(connection, connector),
      connectionRuntimeUrl: (connection as { properties?: { connectionRuntimeUrl?: string } } | undefined)
        ?.properties?.connectionRuntimeUrl,
      isReady: !connectionPending && !connectorPending,
    };
  }, [enabled, connection, connector, connectionPending, connectorPending]);
}
